import type { FilterKey } from '@/types';

interface StatsItem {
  label: string;
  count: number;
}

interface StatsListProps {
  items: StatsItem[];
  color?: string;
  isLoading?: boolean;
  filterKey?: FilterKey;
  onFilter?: (key: FilterKey, value: string) => void;
  onShowMore?: () => void;
  hasMore?: boolean;
}

export function StatsList({ items, color = '#3b82f6', isLoading = false, filterKey, onFilter, onShowMore, hasMore }: StatsListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="animate-spin h-6 w-6 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return <p className="text-gray-500 text-sm">No data available</p>;
  }

  const total = items.reduce((sum, item) => sum + item.count, 0);
  const maxCount = Math.max(...items.map((item) => item.count));

  // Convert hex color to rgba for transparency
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const isClickable = filterKey && onFilter;

  return (
    <div>
      <div className="space-y-2">
        {items.map((item, index) => {
          const percentage = total > 0 ? (item.count / total) * 100 : 0;
          const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;

          return (
            <div
              key={item.label || index}
              className={`flex items-center justify-between text-sm ${
                isClickable ? 'cursor-pointer hover:bg-gray-50 rounded transition-colors' : ''
              }`}
              style={{ minHeight: '32px' }}
              onClick={() => isClickable && onFilter(filterKey, item.label)}
            >
              {/* Label with progress bar */}
              <div className="relative flex-1 mr-4 rounded overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 transition-all duration-300"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: hexToRgba(color, 0.15),
                  }}
                />
                <span className="relative z-10 text-gray-700 truncate block px-2 py-1">
                  {item.label || 'Unknown'}
                </span>
              </div>
              {/* Numbers - no bar behind */}
              <div className="flex items-center gap-3 text-gray-600 flex-shrink-0">
                <span className="font-medium w-12 text-right">
                  {item.count.toLocaleString()}
                </span>
                <span className="w-14 text-right text-gray-500">
                  {percentage.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {hasMore && onShowMore && (
        <button
          onClick={onShowMore}
          className="w-full mt-3 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
        >
          Show more
        </button>
      )}
    </div>
  );
}

export default StatsList;
